import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import {
  controlDoor,
  controlLight,
  controlLightChannels,
  registerDoorOpening,
  saveIotSensorRelays,
  getPowerReservations,
  cancelPowerReservation,
} from '../../../api/iot';
import { errorMessages } from '../../../utils/methods';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import LightingIcon from '../../../_icons/LightingIcon';
import ThermometerIcon from '../../../_icons/ThermometerIcon';
import DisplayControlIcon from '../../../_icons/DisplayControlIcon';
import { cardOf, channelsOfCard, enabledChannelsOfCard, unassignedChannels, enabledChannels, buildChannelMap, presentCards, isOn } from './iotChannels';
import DumbbellIcon from '../../../_icons/DumbbellIcon';
import EtcIcon from '../../../_icons/EtcIcon';
import PowerIcon from '../../../_icons/PowerIcon';
import {
  RightPanel,
  RightPanelHeader,
  RightPanelTitle,
  RightPanelTitleText,
  RightPanelBadge,
  SettingCard,
  SettingCardHeader,
  SettingCardTitle,
  StyledSwitch,
  LightingItemGrid,
  LightingItemBox,
  LightingItemNumber,
  LightingItemDisabledBadge,
  OpenCloseToggle,
  OpenCloseButton,
  DoorSettingCard,
  DoorSettingTitle,
  DoorCardBody,
  DoorCardTop,
  DoorStateText,
  DoorCardLeft,
  DoorOpeningInline,
  DoorOpeningLabel,
  DoorOpeningTimes,
  SmallOutlineButton,
  PowerGroup,
  PowerGroupHeader,
  PowerGroupTitle,
  DetailTabBar,
  DetailTabButton,
} from './FacilityControlStyled';
import DoorSettingModal from './DoorSettingModal';
import DoorOpeningModal from './DoorOpeningModal';
import { sendWithOfflineConfirm, OFFLINE_SEND_MESSAGE, OFFLINE_SAVE_MESSAGE } from './iotOffline';
import DoorTimeSettingModal from './DoorTimeSettingModal';
import ReservationHistoryList from './ReservationHistoryList';
import LightNameEditModal from './LightNameEditModal';
import WeeklySchedule from './WeeklySchedule/WeeklySchedule';

// 카드별 아이콘 / ON 컬러. 냉난방 카드 안에서 AIRCON 파랑, HEATING 빨강
const CARD_ICON = {
  LIGHT: { Icon: LightingIcon, onColor: '#0F7F85' },
  CLIMATE: { Icon: ThermometerIcon, onColor: '#1E88E5' },
  DISPLAY: { Icon: DisplayControlIcon, onColor: '#0F7F85' },
  EQUIPMENT: { Icon: DumbbellIcon, onColor: '#0F7F85' },
  ETC: { Icon: EtcIcon, onColor: '#0F7F85' },
  POWER: { Icon: PowerIcon, onColor: '#0F7F85' },
};
const HEATING_COLOR = '#E53935';

// 제어 후 Elyxion 반영을 기다렸다가 상태 재조회
const STATUS_REFETCH_DELAY_MS = 2000;

// "2026-09-17 21:00:00" → 오늘이면 "21:00", 아니면 "09-18 09:00"
const formatOpenUntil = raw => {
  if (!raw) return null;
  const [date, time = ''] = String(raw).split(/[T ]/);
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return date === todayStr ? time.slice(0, 5) : `${date.slice(5)} ${time.slice(0, 5)}`;
};

const RoomDetailPanel = ({ room, relays = [], sensorData, sensorStatusData, selectedSensorId, scheduleData }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useSelector(state => state.user);
  const token = user?.token;
  const branchId = user?.branch?.branchId;
  const sensorId = selectedSensorId;

  // 현재 룸의 센서 row (숫자 id, 연결 상태, 항시열림 상태)
  const currentSensor = sensorData?.find(sensor => sensor.sensorId === selectedSensorId);
  const numericSensorId = currentSensor?.id;
  const isSensorOffline = currentSensor?.connectYn === 'N';
  const openUntilText = formatOpenUntil(currentSensor?.doorOpenUntil);

  // 릴레이 목록 (이름·사용여부·카테고리)은 FacilityControl 의 룸별 조회를 받는다
  const relayData = relays;
  const relayByChannel = Object.fromEntries(relayData.map(r => [r.relayChannel, r]));
  const channels = sensorStatusData?.channels;
  const channelMap = buildChannelMap(relayData);
  // 우측 전원 카드 그룹 = 채널이 있는 카테고리 카드 + 미배정 "전원". 좌측 카드와 같은 묶음
  const powerGroups = [
    ...presentCards(relayData).map(key => ({ key, title: cardOf(key).label, channels: channelsOfCard(relayData, key), enabled: enabledChannelsOfCard(relayData, key) })),
    { key: 'POWER', title: '전원', channels: unassignedChannels(relayData), enabled: enabledChannels(relayData) },
  ].filter(g => g.channels.length > 0);

  // 출입문 상태 (센서 row 기준)
  const [doorStatus, setDoorStatus] = useState('closed');
  useEffect(() => {
    // 센서가 없거나 목록에 없으면 이전 룸의 열림 상태가 남지 않게 닫힘으로
    setDoorStatus(currentSensor?.doorAlwaysOpenYn === 'Y' ? 'open' : 'closed');
  }, [currentSensor?.doorAlwaysOpenYn, currentSensor?.id]);

  const [doorModalOpen, setDoorModalOpen] = useState(false);
  const [doorOpeningModalOpen, setDoorOpeningModalOpen] = useState(false);
  const [doorTimeModalOpen, setDoorTimeModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [lightNameModalOpen, setLightNameModalOpen] = useState(false);
  const [editingLight, setEditingLight] = useState(null);
  const [detailTab, setDetailTab] = useState('schedule'); // 'schedule' | 'reservation'

  const requireSensor = () => {
    if (!sensorId) {
      toast.error(t('센서 정보가 없습니다'));
      return false;
    }
    return true;
  };

  const refetchStatusLater = () =>
    setTimeout(() => queryClient.invalidateQueries({ queryKey: ['iotSensorStatus', sensorId] }), STATUS_REFETCH_DELAY_MS);

  // 도어 제어 (계속 열기 / 지정 시각까지 / 자동잠김)
  const doorControlMutation = useMutation({
    mutationFn: data => sendWithOfflineConfirm(force => controlDoor(data, token, force), t(OFFLINE_SEND_MESSAGE)),
    onSuccess: (response, variables) => {
      toast.success(t('출입문 제어가 완료되었습니다'));
      setDoorStatus(variables.onOff === 'on' ? 'open' : 'closed');
      setDoorModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['iotSensor', branchId] });
    },
    onError: error => !error?.silent && toast.error(errorMessages(error) || t('출입문 제어에 실패했습니다')),
  });
  const handleDoorOpenConfirm = ({ mode, until }) => {
    if (!requireSensor()) return;
    doorControlMutation.mutate(
      mode === 'time'
        ? { sensorId, onOff: 'on', type: 'time', mode: 'auto', date: until }
        : { sensorId, onOff: 'on', type: 'always', mode: 'manual' },
    );
  };
  const handleDoorLock = () => {
    if (!requireSensor()) return;
    doorControlMutation.mutate({ sensorId, onOff: 'off', type: 'always', mode: 'manual' });
  };

  // 도어 인증 가능 시간
  const doorOpeningSlots = (() => {
    try {
      const parsed = room?.doorOpening ? JSON.parse(room.doorOpening) : [];
      return Array.isArray(parsed) ? parsed.filter(s => s?.start && s?.end) : [];
    } catch {
      return [];
    }
  })();
  const doorOpeningMutation = useMutation({
    mutationFn: opening =>
      sendWithOfflineConfirm(force => registerDoorOpening({ sensorId, opening }, token, force), t(OFFLINE_SAVE_MESSAGE)),
    onSuccess: () => {
      toast.success(t('도어 인증 가능 시간이 등록되었습니다'));
      setDoorOpeningModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['iotRoom', branchId] });
    },
    onError: error => !error?.silent && toast.error(errorMessages(error) || t('도어 인증 가능 시간 등록에 실패했습니다')),
  });

  // 예약 목록 (지점 단위 → 이 룸 센서만)
  const { data: reservationData, isLoading: isReservationLoading } = useQuery({
    queryKey: ['iotPowerReservations', branchId],
    queryFn: () => getPowerReservations(branchId, token),
    enabled: !!branchId && !!token && detailTab === 'reservation',
  });
  const roomReservations = (Array.isArray(reservationData) ? reservationData : []).filter(r => r.sensorId === selectedSensorId);
  const cancelReservationMutation = useMutation({
    mutationFn: reservationId => cancelPowerReservation(reservationId, token),
    onSuccess: () => {
      toast.success(t('예약이 취소되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['iotPowerReservations', branchId] });
    },
    onError: error => toast.error(errorMessages(error) || t('예약 취소에 실패했습니다')),
  });

  // 채널 1개 제어 (낙관적 갱신)
  const lightControlMutation = useMutation({
    mutationFn: data => sendWithOfflineConfirm(force => controlLight(data, token, force), t(OFFLINE_SEND_MESSAGE)),
    onMutate: async data => {
      await queryClient.cancelQueries({ queryKey: ['iotSensorStatus', sensorId] });
      const previousData = queryClient.getQueryData(['iotSensorStatus', sensorId]);
      queryClient.setQueryData(['iotSensorStatus', sensorId], old =>
        old?.channels ? { ...old, channels: { ...old.channels, [data.light]: { onOff: data.onOff } } } : old,
      );
      return { previousData };
    },
    onError: (error, variables, context) => {
      if (!error?.silent) toast.error(errorMessages(error) || t('전원 제어에 실패했습니다'));
      if (context?.previousData) queryClient.setQueryData(['iotSensorStatus', sensorId], context.previousData);
    },
    onSettled: refetchStatusLater,
  });

  // 채널 묶음 제어 — 조명 전체(1~5)
  const channelsControlMutation = useMutation({
    mutationFn: data => sendWithOfflineConfirm(force => controlLightChannels(data, token, force), t(OFFLINE_SEND_MESSAGE)),
    onMutate: async data => {
      await queryClient.cancelQueries({ queryKey: ['iotSensorStatus', sensorId] });
      const previousData = queryClient.getQueryData(['iotSensorStatus', sensorId]);
      queryClient.setQueryData(['iotSensorStatus', sensorId], old => {
        if (!old?.channels) return old;
        const updated = { ...old.channels };
        data.channels.forEach(ch => {
          updated[`l${ch}`] = { onOff: data.onOff };
        });
        return { ...old, channels: updated };
      });
      return { previousData };
    },
    onError: (error, variables, context) => {
      if (!error?.silent) toast.error(errorMessages(error) || t('조명 전체 제어에 실패했습니다'));
      if (context?.previousData) queryClient.setQueryData(['iotSensorStatus', sensorId], context.previousData);
    },
    onSettled: refetchStatusLater,
  });

  const handleChannelToggle = (channel, currentlyOn) => {
    if (!requireSensor()) return;
    lightControlMutation.mutate({ sensorId, light: `l${channel}`, onOff: currentlyOn ? 'off' : 'on' });
  };
  const handleGroupAll = (groupChannels, checked) => {
    if (!requireSensor()) return;
    channelsControlMutation.mutate({ sensorId, channels: groupChannels, onOff: checked ? 'on' : 'off' });
  };
  const controlPending = lightControlMutation.isPending || channelsControlMutation.isPending;

  // 전원명 수정
  const saveLightNameMutation = useMutation({
    mutationFn: data => saveIotSensorRelays(data, token),
    onSuccess: () => {
      toast.success(t('전원명이 수정되었습니다'));
      setLightNameModalOpen(false);
      setEditingLight(null);
      queryClient.invalidateQueries({ queryKey: ['iotSensorRelays', numericSensorId] });
    },
    onError: error => toast.error(errorMessages(error) || t('전원명 수정에 실패했습니다')),
  });
  const handleLightNameEdit = (relayChannel, relay) => {
    setEditingLight({ relayChannel, relayName: relay?.relayName || '', useYn: relay?.useYn || 'Y', category: relay?.category || '' });
    setLightNameModalOpen(true);
  };

  if (!room) {
    return (
      <RightPanel>
        <RightPanelTitleText>{t('레슨룸을 선택해주세요')}</RightPanelTitleText>
      </RightPanel>
    );
  }


  return (
    <RightPanel>
      <RightPanelHeader>
        <RightPanelTitle>
          <RightPanelTitleText>{room.roomNm}</RightPanelTitleText>
        </RightPanelTitle>
        {!room.sensorId ? (
          <RightPanelBadge style={{ backgroundColor: '#E0E0E0', color: '#666' }}>{t('센서 없음')}</RightPanelBadge>
        ) : isSensorOffline ? (
          <RightPanelBadge style={{ backgroundColor: '#E0E0E0', color: '#666' }}>{t('장치 연결 끊김')}</RightPanelBadge>
        ) : (
          <RightPanelBadge>{t('장치 연결됨')}</RightPanelBadge>
        )}
      </RightPanelHeader>

      {/* 출입문 설정 — 제목 | 도어 인증 가능 시간(+수정) | 항시열림/자동잠김 */}
      <DoorSettingCard style={{ marginBottom: '16px' }}>
        <DoorCardBody>
          <DoorCardTop>
            {/* 제목 바로 옆에 항시열림/자동잠김, 오른쪽 끝에 도어 인증 가능 시간 */}
            <DoorCardLeft>
              <DoorSettingTitle>{t('출입문 설정')}</DoorSettingTitle>
              <OpenCloseToggle>
                <OpenCloseButton
                  isActive={doorStatus === 'open'}
                  disabled={doorControlMutation.isPending || !sensorId}
                  onClick={() => sensorId && setDoorModalOpen(true)}>
                  {t('항시열림')}
                </OpenCloseButton>
                <OpenCloseButton
                  isActive={doorStatus === 'closed'}
                  disabled={doorControlMutation.isPending || !sensorId}
                  onClick={() => doorStatus !== 'closed' && handleDoorLock()}>
                  {t('자동잠김')}
                </OpenCloseButton>
              </OpenCloseToggle>
            </DoorCardLeft>
            {/* 도어 인증 가능 시간 = 회원 인증(QR·안면) 허용 시간대. 원격 개방과 무관 */}
            <DoorOpeningInline>
              <AccessTimeIcon style={{ fontSize: 18, color: doorOpeningSlots.length ? '#4AB3BC' : '#B3B3B3' }} />
              <DoorOpeningLabel>{t('도어 인증 가능 시간')}</DoorOpeningLabel>
              <DoorOpeningTimes isSet={doorOpeningSlots.length > 0}>
                {doorOpeningSlots.length === 0
                  ? t('미설정')
                  : doorOpeningSlots.map(s => (s.end < s.start ? `${s.start} ~ ${t('익일')} ${s.end}` : `${s.start} ~ ${s.end}`)).join(', ')}
              </DoorOpeningTimes>
              <SmallOutlineButton disabled={!sensorId} onClick={() => setDoorOpeningModalOpen(true)}>
                {doorOpeningSlots.length === 0 ? t('등록') : t('수정')}
              </SmallOutlineButton>
            </DoorOpeningInline>
          </DoorCardTop>
          {doorStatus === 'open' && (
            <DoorStateText>
              {openUntilText
                ? `${openUntilText}${t('까지 열려 있습니다. 이후 자동으로 잠깁니다.')}`
                : t('자동잠김을 누를 때까지 열려 있습니다.')}
            </DoorStateText>
          )}
        </DoorCardBody>
      </DoorSettingCard>

      {/* 전원 — 조명 / 냉난방 / 전광판 그룹 */}
      <SettingCard>
        <SettingCardHeader>
          <SettingCardTitle>{t('전원')}</SettingCardTitle>
        </SettingCardHeader>
        {powerGroups.length === 0 && <PowerGroupTitle>{t('등록된 릴레이가 없습니다')}</PowerGroupTitle>}
        {powerGroups.map(group => {
          const groupAllOn = group.enabled.length > 0 && group.enabled.every(ch => isOn(channels, ch));
          const { Icon: GroupIcon, onColor } = CARD_ICON[group.key] || CARD_ICON.ETC;
          return (
            <PowerGroup key={group.key}>
              <PowerGroupHeader>
                <PowerGroupTitle>{t(group.title)}</PowerGroupTitle>
                <StyledSwitch
                  size="small"
                  checked={groupAllOn}
                  disabled={controlPending || !channels || group.enabled.length === 0}
                  onChange={e => handleGroupAll(group.enabled, e.target.checked)}
                />
              </PowerGroupHeader>
              <LightingItemGrid>
                {group.channels.map(num => {
                  const on = isOn(channels, num);
                  const relay = relayByChannel[num];
                  const name = channelMap[num].name;
                  const isDisabled = !channelMap[num].enabled;
                  const color = relay?.category === 'HEATING' ? HEATING_COLOR : onColor;
                  const iconColor = isDisabled ? '#BDBDBD' : on ? color : '#999';
                  return (
                    <LightingItemBox
                      key={num}
                      isActive={on && !isDisabled}
                      isDisabled={isDisabled}
                      disabled={controlPending || isDisabled || !channels}
                      onClick={() => !isDisabled && channels && handleChannelToggle(num, on)}>
                      {isDisabled && <LightingItemDisabledBadge>{t('미사용')}</LightingItemDisabledBadge>}
                      <GroupIcon width={20} height={20} color={iconColor} />
                      <LightingItemNumber isActive={on} isDisabled={isDisabled}>
                        {name}
                      </LightingItemNumber>
                      <EditOutlinedIcon
                        fontSize="small"
                        style={{ color: '#999', cursor: 'pointer', marginTop: '4px' }}
                        onClick={e => {
                          e.stopPropagation();
                          handleLightNameEdit(num, relay);
                        }}
                      />
                    </LightingItemBox>
                  );
                })}
              </LightingItemGrid>
            </PowerGroup>
          );
        })}
      </SettingCard>

      {/* 전원 스케줄 / 예약내역 탭 */}
      <DetailTabBar>
        <DetailTabButton isActive={detailTab === 'schedule'} onClick={() => setDetailTab('schedule')}>
          {t('전원 스케줄')}
        </DetailTabButton>
        <DetailTabButton isActive={detailTab === 'reservation'} onClick={() => setDetailTab('reservation')}>
          {t('예약내역')}
        </DetailTabButton>
      </DetailTabBar>

      {detailTab === 'schedule' && (
        <WeeklySchedule
          scheduleData={scheduleData}
          relayData={relayData}
          sensorStatusData={sensorStatusData}
          branchId={branchId}
          roomId={room?.id}
          token={token}
        />
      )}

      {detailTab === 'reservation' && (
        <SettingCard>
          <SettingCardHeader>
            <SettingCardTitle>{t('예약내역')}</SettingCardTitle>
            <SmallOutlineButton
              disabled={!sensorId}
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => {
                setEditingReservation(null);
                setDoorTimeModalOpen(true);
              }}>
              {t('예약 등록')}
            </SmallOutlineButton>
          </SettingCardHeader>
          <ReservationHistoryList
            relays={relayData}
            reservations={roomReservations}
            isLoading={isReservationLoading}
            cancelingId={cancelReservationMutation.isPending ? cancelReservationMutation.variables : null}
            onEdit={item => {
              const periodTime = item.periodTime || 0;
              setEditingReservation({
                id: item.id,
                reservationName: item.reservationName,
                openDate: item.executeAt?.slice(0, 10),
                openTime: item.executeAt?.slice(11, 16),
                durationHour: Math.floor(periodTime / 60),
                durationMinute: periodTime % 60,
                linkage: item.controlType ? 'linked' : 'unlinked',
                selectedLights:
                  item.controlType === 'LIGHT' && item.relayChannels
                    ? item.relayChannels.split(',').map(ch => parseInt(ch.trim(), 10))
                    : relayData.filter(r => r.useYn !== 'N').map(r => r.relayChannel),
              });
              setDoorTimeModalOpen(true);
            }}
            onCancel={item => {
              if (!window.confirm(t('예약을 취소하시겠습니까?'))) return;
              cancelReservationMutation.mutate(item.id);
            }}
          />
        </SettingCard>
      )}

      {/* 항시열림 방식 선택 */}
      <DoorSettingModal
        open={doorModalOpen}
        onClose={() => setDoorModalOpen(false)}
        onConfirm={handleDoorOpenConfirm}
        isPending={doorControlMutation.isPending}
      />

      {/* 도어 인증 가능 시간 */}
      <DoorOpeningModal
        open={doorOpeningModalOpen}
        onClose={() => setDoorOpeningModalOpen(false)}
        doorOpening={room?.doorOpening}
        onSave={opening => doorOpeningMutation.mutate(opening)}
        isPending={doorOpeningMutation.isPending}
      />

      {/* 예약 등록/수정 */}
      <DoorTimeSettingModal
        open={doorTimeModalOpen}
        onClose={() => {
          setDoorTimeModalOpen(false);
          setEditingReservation(null);
        }}
        onSaved={() => {
          setEditingReservation(null);
          queryClient.invalidateQueries({ queryKey: ['iotPowerReservations', branchId] });
        }}
        initialData={editingReservation}
        branchId={branchId}
        sensorId={selectedSensorId}
        token={token}
        relayData={relayData}
      />

      {/* 전원명 수정 */}
      <LightNameEditModal
        open={lightNameModalOpen}
        onClose={() => {
          setLightNameModalOpen(false);
          setEditingLight(null);
        }}
        onSave={data => numericSensorId && saveLightNameMutation.mutate({ sensorId: numericSensorId, relays: [data] })}
        lightData={editingLight}
        isLoading={saveLightNameMutation.isPending}
      />
    </RightPanel>
  );
};

export default RoomDetailPanel;
